// src/services/attribution.ts
/**
 * Remembers where a visitor came from (campaign parameters, external
 * referrer) so the backend can store it on the account at signup. Read by
 * daex-backend/src/common/request-context.ts.
 *
 * The cookie is not technically necessary, so it is only set after the
 * visitor agreed in AttributionConsent (§ 25 Abs. 1 TDDDG). First touch wins:
 * an existing cookie is never overwritten. It is a session cookie and only
 * offered for visits that actually carry campaign data.
 */
const COOKIE_NAME = "daex_attribution";

// Coming back from a social login or payment is not an acquisition channel
const IGNORED_REFERRERS = [
  "accounts.google.com",
  "appleid.apple.com",
  "login.microsoftonline.com",
  "login.live.com",
  "checkout.stripe.com",
];

export type Attribution = {
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  // Ad or web.daex.app variant, e.g. "hero1-b"
  utmContent?: string;
  referrer?: string;
};

let pending: Attribution | null = null;

/**
 * Reads the campaign data of the landing URL. Must run before routing, which
 * may drop the query string. Sets nothing: see acceptAttribution.
 */
export function detectAttribution() {
  try {
    if (hasAttributionCookie()) return;

    const params = new URLSearchParams(window.location.search);
    const attribution: Attribution = {
      utmSource: params.get("utm_source") ?? params.get("ref") ?? undefined,
      utmMedium: params.get("utm_medium") ?? undefined,
      utmCampaign: params.get("utm_campaign") ?? undefined,
      utmContent: params.get("utm_content") ?? undefined,
      referrer: externalReferrerHost(),
    };
    if (
      attribution.utmSource ||
      attribution.utmCampaign ||
      attribution.referrer
    ) {
      pending = attribution;
    }
  } catch {
    // Analytics must never break the app
  }
}

// Campaign data waiting for the visitor's consent, if any
export function pendingAttribution(): Attribution | null {
  return pending;
}

export function acceptAttribution() {
  if (!pending) return;
  try {
    const onDaexDomain = window.location.hostname.endsWith("daex.app");
    const parts = [
      `${COOKIE_NAME}=${encodeURIComponent(JSON.stringify(pending))}`,
      "Path=/",
      // Shared with api.daex.app
      ...(onDaexDomain ? ["Domain=daex.app"] : []),
      // Apple's sign-in returns as a cross-site POST, which Lax would drop
      ...(window.location.protocol === "https:"
        ? ["SameSite=None", "Secure"]
        : ["SameSite=Lax"]),
    ];
    document.cookie = parts.join("; ");
  } catch {
    // Analytics must never break the app
  }
  pending = null;
}

export function declineAttribution() {
  pending = null;
}

function hasAttributionCookie(): boolean {
  return document.cookie
    .split("; ")
    .some((c) => c.startsWith(`${COOKIE_NAME}=`));
}

// Only the host, and only when the visitor came from outside DAEX
function externalReferrerHost(): string | undefined {
  if (!document.referrer) return undefined;
  try {
    const host = new URL(document.referrer).hostname;
    const internal =
      host.endsWith("daex.app") || host === window.location.hostname;
    return internal || IGNORED_REFERRERS.includes(host) ? undefined : host;
  } catch {
    return undefined;
  }
}
